import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { PuckComponent } from "@ease/page-builder/ai";
import type {
  BuilderPageAnalytics,
  OptimizationApplyResult,
  OptimizationSeoInput,
} from "@ease/page-builder/services";

export type RecommendationPriority = "high" | "medium" | "low";
export type RecommendationCategory = "conversion" | "seo" | "performance";
export type RecommendationStatus = "pending" | "dismissed" | "applied";
export type RecommendationSignalStatus = "measured" | "partial" | "missing";
export type RecommendationSurface = "dashboard" | "editor";
export type RecommendationUpliftStatus = "measured" | "directional" | "insufficient" | "missing";

export interface RecommendationAction {
  kind: "apply_canonical_fix" | "apply_seo_meta" | "normalize_slug" | "fill_missing_alt_text";
  label: string;
}

export interface RecommendationUpliftInsight {
  status: RecommendationUpliftStatus;
  winnerId: string | null;
  winnerLabel: string | null;
  metricKey: string;
  metricLabel: string;
  baselineValue: number | null;
  candidateValue: number | null;
  absoluteDelta: number | null;
  relativeLift: number | null;
  sampleSize: {
    baseline: number | null;
    candidate: number | null;
    measuredThreshold: number;
    directionalThreshold: number;
    status: "measured" | "directional" | "insufficient" | "unknown";
    note: string;
  };
  headline: string;
  summary: string;
  caveats: string[];
}

export interface PageRecommendation {
  id: string;
  category: RecommendationCategory;
  priority: RecommendationPriority;
  title: string;
  message: string;
  status: RecommendationStatus;
  confidence: number | null;
  expectedLift: number | null;
  upliftInsight: RecommendationUpliftInsight | null;
  targetComponentId: string | null;
  targetComponentType: string | null;
  signalStatus: RecommendationSignalStatus;
  signalNotes: string[];
  action: RecommendationAction | null;
}

export interface PageHealthComponent {
  componentId: string | null;
  componentType: string;
  directFormSubmissions: number;
  submissionsToday: number;
  analyticsReady: boolean;
  notes: string[];
}

export interface PageHealth {
  pageId: string;
  overallScore: number;
  scoreBreakdown: {
    conversion: number;
    seo: number;
    performance: number | null;
  };
  recommendationCount: number;
  lastUpdated: string;
  telemetry: {
    directConversions: number;
    pageConversionRate: number | null;
    pageViews: number | null;
    bounceRate: number | null;
    avgTimeOnPageSeconds: number | null;
    signalStatus: RecommendationSignalStatus;
    missingSignals: string[];
  };
  audit: {
    auditedAt: string;
    performanceScore: number | null;
    accessibilityScore: number | null;
    seoScore: number | null;
    bestPracticesScore: number | null;
  } | null;
  experiment: {
    experimentId: string;
    status: string;
    totalImpressions: number;
    totalConversions: number;
  } | null;
  components: PageHealthComponent[];
}

export type PageAnalytics = BuilderPageAnalytics;

export interface RecommendationDraftInput {
  components?: PuckComponent[];
  seo?: OptimizationSeoInput;
  pageUrl?: string;
}

export interface RecommendationApplyResult {
  recommendationId: string;
  status: RecommendationStatus;
  action: RecommendationAction["kind"];
  persisted: boolean;
  draft: OptimizationApplyResult | null;
}

function buildRecommendationPath(
  pageId: string,
  surface?: RecommendationSurface,
  componentIds?: readonly string[],
): string {
  const params = new URLSearchParams();
  if (surface) {
    params.set("surface", surface);
  }
  if (componentIds && componentIds.length > 0) {
    params.set("componentIds", componentIds.join(","));
  }
  const query = params.toString();
  return `/crm/pages/${pageId}/recommendations${query ? `?${query}` : ""}`;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  return "Request failed";
}

export function usePageHealth(pageId: string | undefined) {
  const [health, setHealth] = useState<PageHealth | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!pageId) {
      setHealth(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    apiFetch<{ data: PageHealth }>(`/crm/pages/${pageId}/health`)
      .then((res) => {
        if (!cancelled) {
          setHealth(res.data);
        }
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setHealth(null);
          setError(toErrorMessage(fetchError));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pageId, refreshKey]);

  return { health, isLoading, error, refresh };
}

export function usePageAnalytics(pageId: string | undefined) {
  const [analytics, setAnalytics] = useState<PageAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!pageId) {
      setAnalytics(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    apiFetch<{ data: PageAnalytics }>(`/crm/pages/${pageId}/analytics`)
      .then((res) => {
        if (!cancelled) {
          setAnalytics(res.data);
        }
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setAnalytics(null);
          setError(toErrorMessage(fetchError));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pageId, refreshKey]);

  return { analytics, isLoading, error, refresh };
}

export function usePageRecommendations(
  pageId: string | undefined,
  input?: {
    surface?: RecommendationSurface;
    componentIds?: readonly string[];
    draft?: RecommendationDraftInput;
  },
) {
  const [recommendations, setRecommendations] = useState<PageRecommendation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const componentIdsKey = useMemo(
    () => JSON.stringify(input?.componentIds ?? []),
    [input?.componentIds],
  );
  const draftKey = useMemo(
    () => JSON.stringify(input?.draft ?? null),
    [input?.draft],
  );
  const requestPath = useMemo(() => {
    if (!pageId) {
      return null;
    }
    return buildRecommendationPath(pageId, input?.surface, input?.componentIds);
  }, [componentIdsKey, input?.surface, pageId]);

  const refresh = useCallback(() => {
    setRefreshKey((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!pageId || !requestPath) {
      setRecommendations([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const request = input?.draft
      ? apiFetch<{ data: PageRecommendation[] }>(requestPath, {
          method: "POST",
          body: JSON.stringify({
            surface: input.surface,
            componentIds: input.componentIds,
            ...input.draft,
          }),
        })
      : apiFetch<{ data: PageRecommendation[] }>(requestPath);

    request
      .then((res) => {
        if (!cancelled) {
          setRecommendations(res.data ?? []);
        }
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setError(toErrorMessage(fetchError));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [draftKey, input?.surface, pageId, refreshKey, requestPath]);

  const dismiss = useCallback(async (recommendationId: string) => {
    if (!pageId) {
      return null;
    }

    setError(null);
    const response = await apiFetch<{ data: PageRecommendation }>(
      `/crm/pages/${pageId}/recommendations/${recommendationId}/dismiss`,
      { method: "POST" },
    );
    refresh();
    return response.data;
  }, [pageId, refresh]);

  const apply = useCallback(async (recommendationId: string) => {
    if (!pageId) {
      return null;
    }

    setError(null);
    const response = await apiFetch<{ data: RecommendationApplyResult }>(
      `/crm/pages/${pageId}/recommendations/${recommendationId}/apply`,
      {
        method: "POST",
        ...(input?.draft
          ? {
              body: JSON.stringify(input.draft),
            }
          : {}),
      },
    );
    refresh();
    return response.data;
  }, [input?.draft, pageId, refresh]);

  return { recommendations, isLoading, error, dismiss, apply, refresh };
}
